import { BugOutlined, DisconnectOutlined, ReloadOutlined } from '@ant-design/icons';
import StatusScreen from 'publicContainer/StatusScreen';
import React from 'react';
import { Button, Collapse, Typography } from 'zui/Antd';

const { Paragraph } = Typography;

// A deploy replaces the hashed chunks, so a tab opened before it requests files that no longer exist.
const CHUNK_RELOAD_KEY = 'chunkReloadAt';
const CHUNK_RELOAD_WINDOW_MS = 10000; // loop guard: one auto-reload per 10s

const CHUNK_ERROR_PATTERNS = [
	/Loading chunk [\w-]+ failed/i,
	/Loading CSS chunk [\w-]+ failed/i,
	/Failed to fetch dynamically imported module/i,
	/error loading dynamically imported module/i,
	/Importing a module script failed/i
];

const isChunkLoadError = (error) => {
	if (!error) return false;
	const message = error.message || String(error);
	return error.name === 'ChunkLoadError' || CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
};

// Dev chunk errors mean the dev server stopped or re-optimised; reloading into a dead server only hides it.
const shouldAutoReload = (error) => isChunkLoadError(error) && !import.meta.env.DEV && navigator.onLine;

// Reloads at most once per window; false means the guard held, so the user gets the manual prompt.
const tryReloadOnce = () => {
	try {
		const now = Date.now();
		if (now - Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0) <= CHUNK_RELOAD_WINDOW_MS) return false;
		sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
	} catch {
		if (window.__chunkReloadTried) return false;
		window.__chunkReloadTried = true;
	}
	window.location.reload();
	return true;
};

const reload = () => window.location.reload();
const goHome = () => window.location.assign('/');

const ReloadButton = () => (
	<Button type='primary' block icon={<ReloadOutlined />} onClick={reload}>
		Reload page
	</Button>
);

// What the user sees, picked by cause: offline, a missing chunk, or a real fault.
const ErrorScreen = ({ error, reloading }) => {
	if (!navigator.onLine) {
		return (
			<StatusScreen
				tone='warning'
				icon={<DisconnectOutlined />}
				title="You're offline"
				description='Part of the dashboard could not be downloaded. Reconnect to the internet and reload the page.'
				actions={<ReloadButton />}
			/>
		);
	}

	if (isChunkLoadError(error)) {
		return (
			<StatusScreen
				tone='info'
				icon={<ReloadOutlined spin={reloading} />}
				title={reloading ? 'Updating to the latest version' : "This page didn't load"}
				description={
					reloading
						? 'A new version was just released. Refreshing now — this only takes a moment.'
						: import.meta.env.DEV
							? 'The dev server stopped or restarted while this tab was open. Start it again, then reload.'
							: 'Part of the dashboard could not be downloaded, usually because a new version was released. Reloading fixes it.'
				}
				actions={
					!reloading && (
						<>
							<ReloadButton />
							<Button type='link' onClick={goHome}>
								Go to home
							</Button>
						</>
					)
				}
			/>
		);
	}

	return (
		<StatusScreen
			tone='error'
			icon={<BugOutlined />}
			title='Something went wrong'
			description='This page hit an unexpected error. Reloading usually fixes it — if it keeps happening, send the details below to support.'
			actions={
				<>
					<ReloadButton />
					<Button type='link' onClick={goHome}>
						Go to home
					</Button>
					<Collapse
						ghost
						size='small'
						style={{ alignSelf: 'stretch', textAlign: 'left' }}
						items={[
							{
								key: 'details',
								label: 'Technical details',
								children: (
									<Paragraph
										code
										copyable={{ text: error?.stack || String(error) }}
										style={{ margin: 0, fontSize: 12, wordBreak: 'break-word' }}
									>
										{String(error)}
									</Paragraph>
								)
							}
						]}
					/>
				</>
			}
		/>
	);
};

class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { error: null, reloading: false };
	}

	static getDerivedStateFromError(error) {
		return { error };
	}

	componentDidCatch(error, errorInfo) {
		if (shouldAutoReload(error)) {
			this.setState({ reloading: tryReloadOnce() });
			return;
		}
		console.error('Error caught by ErrorBoundary:', error, errorInfo);
	}

	render() {
		if (this.state.error) return <ErrorScreen error={this.state.error} reloading={this.state.reloading} />;
		return this.props.children;
	}
}

export default ErrorBoundary;
